<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import SchoolSetupIcon from '@/assets/icons/SchoolSetupIcon.vue';
import { Components } from '@/api/generated/api';

const props = defineProps<{
  popup: Components.Schemas.GrowthPopupType;
  onDismissPopup?: (popup: Components.Schemas.GrowthPopupType) => Promise<void>;
  onPrimaryFirstEntry?: () => Promise<void>;
  onPrimaryCreateClass?: () => Promise<void>;
  onPrimarySchoolSetup?: () => Promise<void>;
  showFirstEntryPrimary?: boolean;
  onSecondary?: () => Promise<void> | void;
  onDismiss?: () => Promise<void> | void;
  onClose?: () => void;
}>();

const { t } = useI18n();
const dialogRef = useTemplateRef('dialogRef');
const isLoading = ref(false);
const actionHandled = ref(false);

const popupValue = computed<string>(() => props.popup);
const isFirstEntry = computed(() => popupValue.value === 'POPUP_FIRST_ENTRY');
const isCreateClass = computed(() => popupValue.value === 'POPUP_CREATE_CLASS');
const isSchoolSetup = computed(() => popupValue.value === 'POPUP_SCHOOL_SETUP');

const imageSrc = computed(() => {
  if (isFirstEntry.value) return '/assets/icons/plant_add.png';
  if (isCreateClass.value) return '/assets/mail/icons/class-join.png';
  return '';
});

const title = computed(() => {
  if (isFirstEntry.value) return t('growth.popup.firstEntry.title');
  if (isCreateClass.value) return t('growth.popup.createClass.title');
  if (isSchoolSetup.value) return t('growth.popup.schoolSetup.title');
  return '';
});

const body = computed(() => {
  if (isFirstEntry.value) return t('growth.popup.firstEntry.body');
  if (isCreateClass.value) return t('growth.popup.createClass.body');
  if (isSchoolSetup.value) return t('growth.popup.schoolSetup.body');
  return '';
});

const hint = computed(() => {
  if (isFirstEntry.value) return t('growth.popup.firstEntry.hint');
  if (isCreateClass.value) return t('growth.popup.createClass.hint');
  if (isSchoolSetup.value) return t('growth.popup.schoolSetup.hint');
  return '';
});

const primaryLabel = computed(() => {
  if (isFirstEntry.value) {
    return props.showFirstEntryPrimary === false
      ? t('growth.popup.firstEntry.close')
      : t('growth.popup.firstEntry.primary');
  }
  if (isCreateClass.value) return t('growth.popup.createClass.primary');
  if (isSchoolSetup.value) return t('growth.popup.schoolSetup.primary');
  return '';
});

const secondaryLabel = computed(() => {
  if (isFirstEntry.value) return t('growth.popup.firstEntry.secondary');
  if (isCreateClass.value) return t('growth.popup.createClass.secondary');
  if (isSchoolSetup.value) return t('growth.popup.schoolSetup.secondary');
  return '';
});

const showPrimary = computed<boolean>(
  () => isSchoolSetup.value || isCreateClass.value || props.showFirstEntryPrimary !== false,
);

const show = async (): Promise<void> => {
  actionHandled.value = false;
  if (popupValue.value === 'NONE') return;
  await dialogRef.value?.show();
};

const hide = (): void => {
  props.onClose?.();
  dialogRef.value?.hide();
};

const handleClose = async (): Promise<void> => {
  if (!actionHandled.value) {
    actionHandled.value = true;
    isLoading.value = true;
    if (props.onDismiss) {
      await props.onDismiss();
    } else if (props.onDismissPopup && popupValue.value !== 'NONE') {
      await props.onDismissPopup(props.popup);
    }
    isLoading.value = false;
  }
  props.onClose?.();
};

const handlePrimary = async (): Promise<void> => {
  const snapshot = popupValue.value;
  if (snapshot === 'POPUP_FIRST_ENTRY' && props.showFirstEntryPrimary !== false && props.onPrimaryFirstEntry) {
    actionHandled.value = true;
    isLoading.value = true;
    await props.onPrimaryFirstEntry();
    isLoading.value = false;
  } else if (snapshot === 'POPUP_CREATE_CLASS' && props.onPrimaryCreateClass) {
    actionHandled.value = true;
    isLoading.value = true;
    await props.onPrimaryCreateClass();
    isLoading.value = false;
  } else if (snapshot === 'POPUP_SCHOOL_SETUP' && props.onPrimarySchoolSetup) {
    actionHandled.value = true;
    isLoading.value = true;
    await props.onPrimarySchoolSetup();
    isLoading.value = false;
  }
  hide();
};

const handleSecondary = async (): Promise<void> => {
  if (props.onSecondary) {
    actionHandled.value = true;
    isLoading.value = true;
    if (props.onDismissPopup && popupValue.value !== 'NONE') {
      await props.onDismissPopup(props.popup);
    }
    await props.onSecondary();
    isLoading.value = false;
  }
  hide();
};

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    class="w-min-0"
    :show-header="false"
    enter-animation="slide-up"
    disable-internal-hide-logic
    no-padding
    content-width-style="w-auto max-w-[480px]"
    mobile-presentation="bottom-sheet"
    @hide="handleClose"
  >
    <div class="px-lg pb-mobile-safe-bottom-lg pt-lg md:p-[80px]">
      <div class="mx-auto flex w-full max-w-[320px] flex-col items-center gap-xl text-center">
        <SchoolSetupIcon v-if="isSchoolSetup" class="size-[80px]" />
        <img v-if="imageSrc" :src="imageSrc" alt="" class="size-[80px] object-contain" />
        <h2 class="text-title font-medium whitespace-pre-line leading-[165%]">{{ title }}</h2>
        <p class="text-body leading-[170%] whitespace-pre-line">{{ body }}</p>
        <p class="text-footer text-secondary">{{ hint }}</p>
        <div class="flex w-full flex-col gap-md text-center">
          <CustomButton
            v-if="showPrimary"
            class="justify-center"
            theme="primary"
            :disabled="isLoading"
            @click="handlePrimary"
          >
            {{ primaryLabel }}
          </CustomButton>
          <CustomButton class="justify-center" :theme="'secondary'" :disabled="isLoading" @click="handleSecondary">
            {{ secondaryLabel }}
          </CustomButton>
        </div>
      </div>
    </div>
  </DialogBase>
</template>
